// frontend/src/pages/NewUIForgotPassword.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../config/axios';

// Botón gaming reutilizable
function GamingButton({ variant = "secondary", className = "", leftIcon, children, disabled, ...props }) {
    const base =
        "inline-flex items-center justify-center gap-2.5 " +
        "h-12 md:h-14 px-6 md:px-8 " +
        "rounded-[14px] md:rounded-[16px] " +
        "font-bold tracking-wider leading-none " +
        "transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98] " +
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100";

    const variants = {
        primary: "bg-accent text-secondary hover:bg-accent-light border-2 border-accent-dark",
        secondary: "bg-primary text-white hover:bg-primary-light border-2 border-primary-dark",
        ctaBlue: "bg-[#2284DF] text-white hover:bg-[#2A91F4] border-2 border-[#1E75C6]",
        ctaYellow: "bg-[#FCE503] text-[#122436] hover:bg-[#FCE503]/90 border-2 border-[#E5CF03]",
    };

    return (
        <button
            className={`${base} ${variants[variant]} ${className}`}
            disabled={disabled}
            {...props}
        >
            {leftIcon && <span className="inline-flex items-center">{leftIcon}</span>}
            <span className="whitespace-nowrap">{children}</span>
        </button>
    );
}

const NewUIForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Aplicar scope de Acumin
    useEffect(() => {
        document.body.classList.add('acumin-scope');
        return () => {
            document.body.classList.remove('acumin-scope');
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            // Siempre enviamos la solicitud al backend
            await axios.post('/auth/request-password-reset', { email });
            // Siempre mostramos éxito, independientemente de si el correo existe o no
            setSuccess(true);
        } catch (err) {
            // Para errores internos del servidor, seguimos mostrando un error
            // pero no revelamos si el email existe o no
            if (err.response?.status >= 500) {
                setError('Error al procesar la solicitud. Por favor, intentá nuevamente más tarde.');
            } else {
                // Para cualquier otro tipo de error, fingimos éxito para no revelar información
                setSuccess(true);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center pt-24 pb-12 px-4 sm:px-6 lg:px-8"
            style={{
                backgroundImage: 'url(/images/backgrounds/hero-radial-bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div className="w-full max-w-md">
                {/* Card */}
                <div
                    className="rounded-[24px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
                    style={{
                        backgroundColor: '#122436',
                        border: '2px solid rgba(255,255,255,0.55)'
                    }}
                >
                    <div className="px-6 sm:px-8 py-8">
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white text-center mb-6 uppercase tracking-wider">
                            Recuperar Contraseña
                        </h2>

                        {success ? (
                            <div className="text-center space-y-6">
                                {/* Success Icon */}
                                <div className="flex justify-center mb-4">
                                    <div
                                        className="w-16 h-16 rounded-full flex items-center justify-center"
                                        style={{
                                            backgroundColor: 'rgba(34, 132, 223, 0.15)',
                                            border: '3px solid #2284DF'
                                        }}
                                    >
                                        <svg
                                            className="w-8 h-8 text-[#2284DF]"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76"
                                            />
                                        </svg>
                                    </div>
                                </div>

                                <div
                                    className="rounded-lg p-4 mb-4"
                                    style={{
                                        backgroundColor: 'rgba(34, 132, 223, 0.15)',
                                        border: '2px solid #2284DF'
                                    }}
                                >
                                    <p className="text-white/90 font-bold mb-2">
                                        Email enviado
                                    </p>
                                    <p className="text-sm text-white/75">
                                        Si la dirección <strong className="text-white">{email}</strong> está asociada a una cuenta en nuestro sistema,
                                        recibirás un correo con instrucciones para recuperar tu contraseña.
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg p-4 text-left"
                                    style={{
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)'
                                    }}
                                >
                                    <p className="text-xs text-white/70">
                                        Por razones de seguridad, este sistema no indica si una dirección de correo existe o no en nuestra base de datos.
                                        Si estás seguro de tener una cuenta con esta dirección y no recibís el correo dentro de unos minutos,
                                        revisá tu carpeta de spam o intentá con otra dirección de correo que puedas haber utilizado.
                                    </p>
                                </div>

                                <Link to="/login" className="block">
                                    <GamingButton
                                        variant="ctaYellow"
                                        className="w-full text-base uppercase"
                                    >
                                        Volver al Login
                                    </GamingButton>
                                </Link>
                            </div>
                        ) : (
                            <>
                                <p className="text-sm text-white/80 mb-6 text-center">
                                    Ingresá tu dirección de correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.
                                </p>

                                {error && (
                                    <div
                                        className="mb-6 rounded-lg p-4 text-sm"
                                        style={{
                                            backgroundColor: 'rgba(253, 2, 3, 0.15)',
                                            border: '2px solid #FD0203',
                                            color: '#FFB3B3'
                                        }}
                                    >
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    {/* Email Input */}
                                    <div>
                                        <label
                                            htmlFor="email"
                                            className="block text-sm font-bold text-white/90 mb-2 uppercase tracking-wide"
                                        >
                                            Email
                                        </label>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            autoComplete="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full px-4 py-3 rounded-lg bg-[#0E2238] border-2 border-white/20 
                                                     text-white placeholder-white/40
                                                     focus:border-[#2284DF] focus:outline-none focus:ring-2 focus:ring-[#2284DF]/50
                                                     transition-all"
                                            placeholder="tu@email.com"
                                        />
                                    </div>

                                    {/* Submit Button */}
                                    <div className="pt-2">
                                        <GamingButton
                                            type="submit"
                                            variant="ctaYellow"
                                            className="w-full text-base uppercase"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Enviando...' : 'Enviar Instrucciones'}
                                        </GamingButton>
                                    </div>
                                </form>

                                <div className="mt-6 text-center">
                                    <Link
                                        to="/login"
                                        className="text-sm text-[#2284DF] hover:text-[#2A91F4] font-semibold transition-colors"
                                    >
                                        Volver al Inicio de Sesión
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Back to home link */}
                <div className="mt-6 text-center">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors font-semibold"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            />
                        </svg>
                        Volver al inicio
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default NewUIForgotPassword;